import { useState } from "react";
import { FiCheckCircle, FiLifeBuoy, FiX } from "react-icons/fi";
import { MfaIconBadge } from "./MfaUi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { authService } from "./mfa.api";

type Props = {
  onClose: () => void;
};

const validateEmail = (email: string): string => {
  if (!email || email.trim() === "") {
    return "Email is required";
  }

  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

  if (!regex.test(email)) {
    return "Please enter a valid email address";
  }

  if (email.length > 100) {
    return "Email is too long";
  }

  return "";
};

const RecoveryModal = ({ onClose }: Props) => {

  const [email, setEmail] = useState<string>("");
  const [reason, setReason] = useState<string>("lost device");

  const [emailError, setEmailError] = useState<string>("");
  const [generalError, setGeneralError] = useState<string>("");

  const [success, setSuccess] = useState<boolean>(false);

  const navigate = useNavigate();

  // 🔥 React Query
  const { mutateAsync, isLoading: loading } = useMutation({
    mutationFn: (payload: { email: string; reason: string }) =>
      authService.requestMfaRecovery(payload),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setEmailError("");
    setGeneralError("");

    // ✅ FIXED validation
    const error = validateEmail(email);

    if (error) {
      setEmailError(error);
      return;
    }

    if (!reason || reason.trim().length < 10) {
      setGeneralError("Please describe your issue (minimum 10 characters)");
      return;
    }

    try {
      const response: any = await mutateAsync({ email, reason });

      toast.success(
        response?.data?.message || "Recovery request submitted successfully"
      );

      setSuccess(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };
  const onChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    setGeneralError("");
  };

  const onCloseRequestModal = () => {
    setEmail("");
    setReason("lost device");
    setEmailError("");
    setGeneralError("");
    setSuccess(false);

    onClose();
    navigate("/");
  };

  return (
    <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="mfa-recovery-title">
      <div className="zs-modal max-w-md">
        {/* Close */}
        <button onClick={onCloseRequestModal} className="zs-icon-btn zs-modal-close" aria-label="Close">
          <FiX size={18} />
        </button>

        {success ? (
          <div className="flex flex-col items-center px-6 py-8 text-center">
            <MfaIconBadge tone="success" size="lg">
              <FiCheckCircle />
            </MfaIconBadge>

            <h2 id="mfa-recovery-title" className="mt-4 text-lg font-semibold font-display text-ink">
              Request submitted
            </h2>

            <p className="max-w-xs mt-2 text-sm text-ink-3">
              If an account exists with this email, we’ll send recovery instructions.
            </p>

            <button onClick={onClose} className="mt-6 zs-btn zs-btn-primary zs-btn-block">
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start gap-3 px-5 pt-5 pb-4 pr-14 border-b sm:px-6 border-line">
              <MfaIconBadge>
                <FiLifeBuoy />
              </MfaIconBadge>
              <div className="min-w-0">
                <h2 id="mfa-recovery-title" className="text-base font-semibold font-display text-ink">
                  Recover your account
                </h2>
                <p className="mt-1 text-sm text-ink-3">
                  Lost access to your authenticator? We’ll help you regain access securely.
                </p>
              </div>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-4 px-5 py-5 sm:px-6">
                {/* Email */}
                <div>
                  <label className="zs-label zs-label-required">Registered Email</label>

                  <input
                    type="email"
                    placeholder="example@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    className={`zs-input ${emailError ? "is-invalid" : ""}`}
                  />

                  {emailError && <p className="zs-error">{emailError}</p>}
                </div>

                {/* Reason */}
                <div>
                  <label className="zs-label zs-label-required">Describe your issue</label>

                  <textarea
                    value={reason}
                    onChange={onChangeText}
                    rows={4}
                    maxLength={300}
                    className="resize-none zs-textarea"
                  />

                  <div className="flex justify-between gap-3 zs-hint">
                    <span>Be as detailed as possible</span>
                    <span className="tabular-nums">{reason.length}/300</span>
                  </div>
                </div>

                {/* Error */}
                {generalError && (
                  <p className="px-3 py-2 text-sm rounded text-danger bg-danger-soft">{generalError}</p>
                )}

                <p className="text-xs text-ink-4">We may verify your identity before granting access.</p>
              </div>

              {/* Submit */}
              <div className="flex justify-end px-5 py-4 border-t sm:px-6 border-line bg-surface-2 rounded-b-xl">
                <button type="submit" disabled={loading} className="w-full zs-btn zs-btn-primary sm:w-auto">
                  {loading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default RecoveryModal;
